import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from 'apollo-react/components/Grid';
import CircularProgress from 'apollo-react/components/CircularProgress';
import { Formik, Form, Field } from 'formik';
import PrivilegesSections from '../../Admin/Roles/PrivilegesSections';
import { renderInputComponent } from '../Form/renderInputComponent';
import ButtonCommon from '../Button';
import RoleChangeConfirmModal from '../RoleChangeConfirmModal/RoleChangeConfirmModal';
import useEditablePrivileges from '../../../hooks/features/privileges/useEditablePrivileges';
import { capitalizeFirstLetter } from '../../utils/stringUtils';
import ProfileEditActions from './sections/ProfileEditActions';
import styles from './Profile.module.css';

const EditablePrivilegesSection = ({
  userId,
  privilegesUserId,
  initialRoleId,
  roleOptions,
  onSave,
  isInternal = true,
  onDirtyChange,
  saveRef,
  categoryName,
  categoryOptions = [],
  initialCategoryId = '',
  editing = false,
  onStartEdit,
  onStopEdit,
  onDiscard,
  canEdit = true,
}) => {
  const { t } = useTranslation();

  const [collapseRevision, setCollapseRevision] = useState(0);

  const {
    loading,
    saving,
    privileges,
    sections,
    selectedPrivileges,
    boxesState,
    isSectionSelected,
    hasCustomPrivileges,
    isDirty,
    selectedRoleId,
    selectedCategoryId,
    showConfirmModal,
    handleRoleChange,
    handleStrategySelect,
    handleCancelRoleChange,
    handleCategoryChange,
    handleResetToRole,
    handleCheckboxChange,
    handleSectionSelection,
    handleSectionClearAll,
    handleSelectAll,
    handleSave,
    restoreToSaved,
  } = useEditablePrivileges({
    userId,
    privilegesUserId,
    initialRoleId,
    initialCategoryId,
    onSave,
    isInternal,
  });

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(editing && isDirty);
  }, [isDirty, onDirtyChange, editing]);

  useEffect(() => {
    if (saveRef) saveRef.current = handleSave;
  }, [saveRef, handleSave]);

  const roleField = useMemo(
    () => ({
      name: 'role',
      type: 'dropdown',
      label: t('Role'),
      options: roleOptions.map((role) => ({
        key: role.id,
        label: role.name,
      })),
    }),
    [t, roleOptions]
  );

  const categoryField = useMemo(() => {
    const normalizedOptions = Array.isArray(categoryOptions)
      ? categoryOptions
      : [];
    let options = normalizedOptions.map((category) => ({
      key: category.id,
      label: category.name,
    }));

    if (options.length === 0) {
      if (initialCategoryId && categoryName) {
        options = [{ key: initialCategoryId, label: categoryName }];
      } else {
        options = [
          {
            key: '',
            label: t('no_categories_available', 'No categories available'),
            disabled: true,
          },
        ];
      }
    }

    return {
      name: 'category',
      type: 'dropdown',
      label: t('category'),
      options,
    };
  }, [t, categoryOptions, initialCategoryId, categoryName]);

  const formData = useMemo(
    () => ({ role: selectedRoleId, category: selectedCategoryId }),
    [selectedRoleId, selectedCategoryId]
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Formik
      initialValues={{ role: selectedRoleId, category: selectedCategoryId }}
      enableReinitialize
    >
      {({ setFieldValue }) => (
        <Form>
          <div className={styles.divider} />

          <Grid container spacing={3}>
            {!isInternal && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field name="category">
                  {({ field: formikField, meta }) =>
                    renderInputComponent(
                      { ...categoryField, disabled: !editing },
                      formData,
                      formikField,
                      meta,
                      (value) => {
                        if (editing) {
                          setFieldValue('category', value);
                          handleCategoryChange(value);
                        }
                      }
                    )
                  }
                </Field>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: isInternal ? 12 : 6 }}>
              <Field name="role">
                {({ field: formikField, meta }) =>
                  renderInputComponent(
                    { ...roleField, disabled: !editing },
                    formData,
                    formikField,
                    meta,
                    (value) => {
                      if (editing) {
                        setFieldValue('role', value);
                        handleRoleChange(value);
                      }
                    }
                  )
                }
              </Field>
            </Grid>
          </Grid>

          {hasCustomPrivileges && (
            <div className={styles.customPrivilegesWarning}>
              {t('privilegesSection.customPrivilegesWarning')}
            </div>
          )}

          <div className={styles.divider} />

          <PrivilegesSections
            sections={sections}
            isSectionSelected={isSectionSelected}
            published={false}
            privileges={privileges}
            boxesState={boxesState}
            handleSectionClearAll={handleSectionClearAll}
            handleSectionSelection={handleSectionSelection}
            handleCheckboxChange={handleCheckboxChange}
            capitalizeFirstLetter={capitalizeFirstLetter}
            selectedPrivileges={selectedPrivileges}
            handleSelectAll={handleSelectAll}
            onReset={handleResetToRole}
            readOnly={!editing}
            collapseRevision={collapseRevision}
          />

          {!editing ? (
            canEdit && (
              <div className={styles.actionsRight}>
                <ButtonCommon
                  color="primary"
                  className={styles.btnSized}
                  onClick={() => {
                    if (onStartEdit) onStartEdit();
                  }}
                  text={t('edit')}
                />
              </div>
            )
          ) : (
            <ProfileEditActions
              onDiscard={() => {
                restoreToSaved();
                setCollapseRevision((n) => n + 1);
                if (onDiscard) onDiscard();
              }}
              onSave={async () => {
                const success = await handleSave();
                if (success) {
                  setCollapseRevision((n) => n + 1);
                  if (onStopEdit) onStopEdit();
                }
              }}
              saveText={saving ? t('saving') : t('Save')}
              saveDisabled={saving}
            />
          )}

          <RoleChangeConfirmModal
            open={showConfirmModal}
            onClose={handleCancelRoleChange}
            onSelectStrategy={handleStrategySelect}
          />
        </Form>
      )}
    </Formik>
  );
};

export default EditablePrivilegesSection;
